/* ==========================================================================
   PILA — Learning Mesh (web / Shopify port)

   Port of the app's LearningMesh (LearningMesh.web.tsx) to a framework-free
   custom element so it can back a section hero.

   LOOP_MS, makeNodes() and nodePoints() below are copied verbatim from
   @/components/learningMeshNodes, TypeScript annotations stripped. Same seeded
   RNG, so the hero mesh is the same mesh the app draws, not a lookalike.

   Two field shapes:
     shape="disc"  the app's circular field, exactly as the app renders it.
     shape="rect"  the same drift and the same seeded parameters, with base
                   positions spread over the element's rectangle instead of a
                   disc. Default for heroes: a 1440x700 hero cropped out of a
                   circular field leaves the corners bare.

   Not ported: the progress ring (RING_STROKE and the ring arc). It is a
   product-UI affordance and would read as noise behind hero copy.

   Usage:
     <pila-learning-mesh color="#F8E08E" nodes="40" opacity="0.4" link="0.3">
   ========================================================================== */

(function () {
  'use strict';

  if (!('customElements' in window)) return;

  var TAU = Math.PI * 2;

  // Node count and connection distance are both tuned against a 1440x700 hero
  // and scaled from there.
  var REFERENCE_AREA = 1440 * 700;
  var LINK_K = 4.4;

  /* ======================================================================
     VERBATIM FROM @/components/learningMeshNodes
     Keep in sync with the app. Nothing outside this block depends on how
     these work, only on their signatures.
     ====================================================================== */

  // One seamless pass of the drift loop
  var LOOP_MS = 40000;

  // Deterministic node orbits: integer frequencies over a 0→1 clock make the
  // loop seamless, and a fixed seed keeps every card's mesh identical.
  function makeNodes(count) {
    var seed = 9;
    var rnd = function () {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };
    return Array.from({ length: count }, function () {
      var angle = rnd() * Math.PI * 2;
      var radius = 0.2 + rnd() * 0.6;
      return {
        bx: Math.cos(angle) * radius,
        by: Math.sin(angle) * radius,
        ax: 0.1 + rnd() * 0.15,
        ay: 0.1 + rnd() * 0.15,
        fx: 1 + Math.floor(rnd() * 3),
        fy: 1 + Math.floor(rnd() * 3),
        px: rnd(),
        py: rnd(),
      };
    });
  }

  // Position all nodes at clock t (0..1), clamped inside the mesh radius.
  function nodePoints(nodes, t, c, meshR) {
    var tau = Math.PI * 2;
    return nodes.map(function (n) {
      var x = n.bx + n.ax * Math.sin(tau * (n.fx * t + n.px));
      var y = n.by + n.ay * Math.sin(tau * (n.fy * t + n.py));
      var len = Math.sqrt(x * x + y * y);
      if (len > 0.92) {
        x = (x / len) * 0.92;
        y = (y / len) * 0.92;
      }
      return { x: c + meshR * x, y: c + meshR * y };
    });
  }

  /* ======================================================================
     RECT FIELD — same seeded RNG, same drift terms, base positions laid out
     across the rectangle on a jittered grid rather than inside a disc, and
     clamped to the box instead of to radius 0.92.
     ====================================================================== */

  function makeRectNodes(count) {
    var seed = 9;
    var rnd = function () {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    var cols = Math.max(1, Math.ceil(Math.sqrt(count * 2)));
    var rows = Math.max(1, Math.ceil(count / cols));

    return Array.from({ length: count }, function (_, i) {
      return {
        bx: ((i % cols) + 0.15 + rnd() * 0.7) / cols,
        by: (Math.floor(i / cols) + 0.15 + rnd() * 0.7) / rows,
        // Halved because bx/by span 0..1 here rather than -0.92..0.92.
        ax: (0.1 + rnd() * 0.15) / 2,
        ay: (0.1 + rnd() * 0.15) / 2,
        fx: 1 + Math.floor(rnd() * 3),
        fy: 1 + Math.floor(rnd() * 3),
        px: rnd(),
        py: rnd(),
      };
    });
  }

  function rectPoints(nodes, t, w, h) {
    return nodes.map(function (n) {
      var x = n.bx + n.ax * Math.sin(TAU * (n.fx * t + n.px));
      var y = n.by + n.ay * Math.sin(TAU * (n.fy * t + n.py));
      return {
        x: Math.max(0.02, Math.min(0.98, x)) * w,
        y: Math.max(0.02, Math.min(0.98, y)) * h,
      };
    });
  }

  /* --------------------------------------------------------------------- */

  var prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class PilaLearningMesh extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) return;

      this.shape = this.getAttribute('shape') === 'disc' ? 'disc' : 'rect';
      this.color = this.getAttribute('color') || '#F8E08E';
      this.lineAlpha = parseFloat(this.getAttribute('opacity') || '0.4');
      this.dotR = parseFloat(this.getAttribute('dot-radius') || '1.8');
      this.cover = parseFloat(this.getAttribute('cover') || '1.15');
      // Connection distance as a fraction of the field's short side. The app
      // uses 0.62 at badge size; a hero is a far bigger field, so the same
      // ratio draws struts right across the headline. Hero default is 0.3.
      this.link = parseFloat(this.getAttribute('link') || '0.62');

      // Node count is a ceiling, not a constant. A phone hero is a fifth of
      // the area of a desktop one, and 40 nodes in that space connect to
      // everything: the mesh turns into a solid web. Scale with the square
      // root of area so perceived density stays put.
      this.baseCount = parseInt(this.getAttribute('nodes') || '40', 10);
      this.count = 0;
      this.nodes = [];

      this.raf = null;
      this.visible = true;
      this.onFrame = this.onFrame.bind(this);
      this.measure = this.measure.bind(this);
      this.onVisibility = this.onVisibility.bind(this);

      this.measure();

      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this.measure);
        this.ro.observe(this);
      } else {
        window.addEventListener('resize', this.measure);
      }

      if (window.IntersectionObserver) {
        this.io = new IntersectionObserver(
          function (entries) {
            this.visible = entries[0].isIntersecting;
            if (this.visible) this.start();
            else this.stop();
          }.bind(this),
          { rootMargin: '120px' }
        );
        this.io.observe(this);
      }

      document.addEventListener('visibilitychange', this.onVisibility);

      if (prefersReduced) this.draw(0); // one static frame
      else this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      window.removeEventListener('resize', this.measure);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this._booted = false;
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else if (this.visible) this.start();
    }

    start() {
      if (prefersReduced || this.raf !== null) return;
      this.raf = requestAnimationFrame(this.onFrame);
    }

    stop() {
      if (this.raf === null) return;
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    measure() {
      var rect = this.getBoundingClientRect();
      var w = Math.max(1, Math.round(rect.width));
      var h = Math.max(1, Math.round(rect.height));
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      this.w = w;
      this.h = h;
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
      this.canvas.style.width = w + 'px';
      this.canvas.style.height = h + 'px';
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Disc mode draws into a centred square that covers the rect, so
      // nodePoints() keeps the app's (nodes, t, c, meshR) signature and the
      // overflow is simply clipped by the host.
      this.side = Math.max(w, h) * this.cover;
      this.offX = (w - this.side) / 2;
      this.offY = (h - this.side) / 2;

      var scaled = Math.round(this.baseCount * Math.sqrt((w * h) / REFERENCE_AREA));
      var count = Math.max(8, Math.min(this.baseCount, scaled));
      if (count !== this.count) {
        this.count = count;
        this.nodes = this.shape === 'disc' ? makeNodes(count) : makeRectNodes(count);
      }

      if (prefersReduced) this.draw(0);
    }

    field(t) {
      if (this.shape === 'disc') {
        var c = this.side / 2;
        var meshR = c - 2; // app: hasRing ? c - 10 : c - 2, and there is no ring here
        return { pts: nodePoints(this.nodes, t, c, meshR), threshold: meshR * this.link };
      }
      // Threshold from mean node spacing rather than from the viewport, so a
      // narrow window gets the same visual density as a wide one instead of a
      // solid web. LINK_K calibrates link=0.3 to the desktop look.
      var spacing = Math.sqrt((this.w * this.h) / Math.max(1, this.count));
      return {
        pts: rectPoints(this.nodes, t, this.w, this.h),
        threshold: this.link * LINK_K * spacing,
      };
    }

    onFrame(now) {
      this.draw((now % LOOP_MS) / LOOP_MS);
      this.raf = requestAnimationFrame(this.onFrame);
    }

    draw(t) {
      var ctx = this.ctx;
      var field = this.field(t);
      var pts = field.pts;
      var threshold = field.threshold;

      ctx.clearRect(0, 0, this.w, this.h);
      ctx.save();
      if (this.shape === 'disc') ctx.translate(this.offX, this.offY);

      ctx.strokeStyle = this.color;
      ctx.globalAlpha = this.lineAlpha;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (var i = 0; i < pts.length; i++) {
        for (var j = i + 1; j < pts.length; j++) {
          var dx = pts[i].x - pts[j].x;
          var dy = pts[i].y - pts[j].y;
          if (Math.sqrt(dx * dx + dy * dy) < threshold) {
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
          }
        }
      }
      ctx.stroke();

      ctx.globalAlpha = 1;
      ctx.fillStyle = this.color;
      for (var k = 0; k < pts.length; k++) {
        ctx.beginPath();
        ctx.arc(pts[k].x, pts[k].y, this.dotR, 0, TAU);
        ctx.fill();
      }

      ctx.restore();
    }
  }

  if (!customElements.get('pila-learning-mesh')) {
    customElements.define('pila-learning-mesh', PilaLearningMesh);
  }
})();
